/**
 * Performance Chart Utilities
 *
 * Helper functions for chart calculations and formatting.
 */

import { CHART_CONFIG } from "./constants";
import type { PerformanceDataPoint, ChartPoint, ValueRange, ChartDimensions } from "./types";

/**
 * Calculate chart inner dimensions
 */
export function getChartDimensions(): ChartDimensions {
  const innerWidth =
    CHART_CONFIG.width - CHART_CONFIG.paddingLeft - CHART_CONFIG.paddingRight;
  const innerHeight =
    CHART_CONFIG.height - CHART_CONFIG.paddingTop - CHART_CONFIG.paddingBottom;
  return { innerWidth, innerHeight };
}

/**
 * Format date for display
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Format month label
 */
export function formatMonthLabel(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short" });
}

/**
 * Format currency value
 */
export function formatCurrency(value: number): string {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)}Cr`;
  }
  if (value >= 100000) {
    return `${(value / 100000).toFixed(2)}L`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}

/**
 * Format percentage change
 */
export function formatPercentage(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

/**
 * Calculate Y-axis value range with padding
 */
export function calculateValueRange(
  data: PerformanceDataPoint[],
  investedAmount: number
): ValueRange {
  if (data.length === 0) {
    return { min: investedAmount * 0.9, max: investedAmount * 1.1 };
  }

  const allValues = [
    ...data.map((d) => d.fundValue),
    ...data.map((d) => d.benchmarkValue),
    investedAmount,
  ];

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min;
  const padding = range * 0.1;

  return {
    min: Math.max(0, min - padding),
    max: max + padding,
  };
}

/**
 * Convert value to Y coordinate
 */
export function valueToY(value: number, min: number, max: number): number {
  const { innerHeight } = getChartDimensions();
  const range = max - min;
  if (range === 0) return CHART_CONFIG.paddingTop + innerHeight / 2;
  return CHART_CONFIG.paddingTop + ((max - value) / range) * innerHeight;
}

/**
 * Convert data index to X coordinate
 */
export function indexToX(index: number, totalPoints: number): number {
  const { innerWidth } = getChartDimensions();
  if (totalPoints <= 1) return CHART_CONFIG.paddingLeft + innerWidth / 2;
  return CHART_CONFIG.paddingLeft + (index / (totalPoints - 1)) * innerWidth;
}

/**
 * Generate smooth SVG path from points
 */
export function generateSmoothPath(points: ChartPoint[]): string {
  if (points.length === 0) return "";
  const firstPoint = points[0];
  if (!firstPoint) return "";
  if (points.length === 1) return `M ${firstPoint.x},${firstPoint.y}`;

  let path = `M ${firstPoint.x},${firstPoint.y}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (!prev || !curr) continue;

    // Simple line for fewer points, smooth curve for more
    if (points.length <= 5) {
      path += ` L ${curr.x},${curr.y}`;
    } else {
      const tension = 0.2;
      const prevPrev = points[i - 2] || prev;
      const next = points[i + 1] || curr;

      const cp1x = prev.x + (curr.x - prevPrev.x) * tension;
      const cp1y = prev.y + (curr.y - prevPrev.y) * tension;
      const cp2x = curr.x - (next.x - prev.x) * tension;
      const cp2y = curr.y - (next.y - prev.y) * tension;

      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
    }
  }

  return path;
}

/**
 * Generate area fill path (closed path under the line)
 */
export function generateAreaPath(points: ChartPoint[], baseY: number): string {
  if (points.length === 0) return "";

  const linePath = generateSmoothPath(points);
  const lastPoint = points[points.length - 1];
  const firstPoint = points[0];

  if (!lastPoint || !firstPoint) return "";

  return `${linePath} L ${lastPoint.x},${baseY} L ${firstPoint.x},${baseY} Z`;
}
